// ── BCHNostr — podcast hooks ──────────────────────────────────────
//
// Three concerns, kept separate:
//
//   useBCHPodcast      one creator's show event (author-filtered)
//   usePodcastFeed     the episodes, read from cache, refreshed lazily
//   usePodcastDirectory  every show, for discovery
//
// The show event is addressable and user-owned, so `d`-tag alone is NOT a
// trust boundary — every single-show read filters by `authors`. The
// directory is public UGC and is intentionally unfiltered, but each event
// is parsed (and re-sanitized) per-author with newest-wins.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Event } from 'nostr-tools';
import { useNostrQuery } from './useNostrQuery';
import { useNostrPublish } from './useNostrPublish';
import { useAppContext } from './useAppContext';
import {
  PODCAST_KIND, PODCAST_D_TAG, PODCAST_TAG, PODCAST_CAT_PREFIX,
  PODCAST_FOLLOW_KIND, PODCAST_FOLLOW_D_TAG, PODCAST_FOLLOW_TAG,
  PODCAST_BOOST_KIND, PODCAST_BOOST_TAG,
  parsePodcast, buildPodcastFollowEvent, parsePodcastFollows,
  getCachedFeed, isCacheStale, refreshFeed, activePodcastBoosts,
  type PodcastShow, type PodcastEpisode, type FeedErrorKind, type PodcastBoostData,
} from '../lib/podcast';

// ── One creator's show ────────────────────────────────────────────

export interface UsePodcastResult {
  podcast: PodcastShow | null;
  loading: boolean;
  refetch: () => void;
}

/** Load the podcast show published by `pubkey`. Author-filtered. */
export function useBCHPodcast(pubkey: string | undefined): UsePodcastResult {
  const { events, loading, refetch } = useNostrQuery({
    filters: pubkey
      ? [{ kinds: [PODCAST_KIND], authors: [pubkey], '#d': [PODCAST_D_TAG], limit: 1 }]
      : [],
    enabled: !!pubkey,
    allRelays: true,
  });

  const podcast = useMemo(() => {
    if (events.length === 0) return null;
    // Newest wins — 30078 is replaceable.
    const newest = [...events].sort((a, b) => b.created_at - a.created_at)[0];
    return parsePodcast(newest);
  }, [events]);

  return { podcast, loading, refetch };
}

// ── Episodes ──────────────────────────────────────────────────────

export interface UsePodcastFeedResult {
  episodes: PodcastEpisode[];
  /** True only on the very first load with no cached episodes to show. */
  loading: boolean;
  /** Set when the most recent fetch failed. Episodes may still be present. */
  error: FeedErrorKind | null;
  /** Unix ms of the last SUCCESSFUL fetch. 0 if never. */
  updatedAt: number;
  /** True while a background refresh is running. */
  refreshing: boolean;
  /** Force a refresh, ignoring the cache interval. */
  refresh: () => void;
}

/**
 * Read a podcast's episodes.
 *
 * Cache-first: cached episodes render immediately and a refresh only fires
 * when the entry is stale, so navigating around the app never re-fetches a
 * feed. A failing feed keeps serving its last good episodes and surfaces
 * `error` for the UI to mention — it never blanks the page or throws.
 */
export function usePodcastFeed(feedUrl: string | undefined): UsePodcastFeedResult {
  const cached = useMemo(() => (feedUrl ? getCachedFeed(feedUrl) : null), [feedUrl]);

  const [episodes, setEpisodes] = useState<PodcastEpisode[]>(() => cached?.episodes || []);
  const [error, setError] = useState<FeedErrorKind | null>(() => cached?.error || null);
  const [updatedAt, setUpdatedAt] = useState<number>(() => cached?.succeededAt || 0);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(() => !!feedUrl && !cached);
  const mountedRef = useRef(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const run = useCallback(async (url: string) => {
    setRefreshing(true);
    const entry = await refreshFeed(url);
    if (!mountedRef.current) return;
    setRefreshing(false);
    setLoading(false);
    if (!entry) {
      setError('invalid-url');
      return;
    }
    setEpisodes(entry.episodes);
    setError(entry.error);
    setUpdatedAt(entry.succeededAt);
  }, []);

  useEffect(() => {
    if (!feedUrl) {
      setEpisodes([]);
      setError(null);
      setLoading(false);
      return;
    }

    const entry = getCachedFeed(feedUrl);
    if (entry) {
      setEpisodes(entry.episodes);
      setError(entry.error);
      setUpdatedAt(entry.succeededAt);
      setLoading(false);
    } else {
      setEpisodes([]);
      setLoading(true);
    }

    // Only hit the network when the cache is actually stale (or when the
    // caller explicitly bumped the nonce via refresh()).
    if (nonce > 0 || isCacheStale(entry)) {
      run(feedUrl);
    }
  }, [feedUrl, nonce, run]);

  const refresh = useCallback(() => setNonce(n => n + 1), []);

  return { episodes, loading, error, updatedAt, refreshing, refresh };
}

// ── Discovery ─────────────────────────────────────────────────────

export interface UsePodcastDirectoryResult {
  podcasts: PodcastShow[];
  loading: boolean;
  refetch: () => void;
}

/** Every published podcast show, optionally narrowed to one category. */
export function usePodcastDirectory(category?: string): UsePodcastDirectoryResult {
  const filters = useMemo(() => {
    const t = category && category !== 'all'
      ? [PODCAST_TAG, `${PODCAST_CAT_PREFIX}${category}`]
      : [PODCAST_TAG];
    return [{ kinds: [PODCAST_KIND], '#t': t, limit: 200 }];
  }, [category]);

  const { events, loading, refetch } = useNostrQuery({ filters, allRelays: true });

  const podcasts = useMemo(() => {
    // One show per author, newest wins.
    const byAuthor = new Map<string, Event>();
    for (const ev of events) {
      const existing = byAuthor.get(ev.pubkey);
      if (!existing || ev.created_at > existing.created_at) byAuthor.set(ev.pubkey, ev);
    }
    return [...byAuthor.values()]
      .map(parsePodcast)
      .filter((p): p is PodcastShow => p !== null)
      // A category filter uses an indexed tag, but a malicious event could
      // carry several — re-check client-side so the tab never lies.
      .filter(p => !category || category === 'all' || p.category === category)
      .sort((a, b) => b.created_at - a.created_at);
  }, [events, category]);

  return { podcasts, loading, refetch };
}

// ── Follows ───────────────────────────────────────────────────────

export interface UsePodcastFollowsResult {
  /** Creator pubkeys whose shows the logged-in user follows. */
  following: string[];
  loading: boolean;
  isFollowing: (pubkey: string) => boolean;
  /** Toggle a follow. Resolves true when the publish succeeded. */
  toggle: (pubkey: string) => Promise<boolean>;
  /** True while a toggle is in flight. */
  saving: boolean;
}

/**
 * The logged-in user's podcast subscriptions.
 *
 * Deliberately separate from kind 3: following a show should not silently
 * rewrite the user's social graph, and losing a podcast list is a far less
 * severe failure than losing a contact list.
 */
export function usePodcastFollows(): UsePodcastFollowsResult {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const [saving, setSaving] = useState(false);
  const [optimistic, setOptimistic] = useState<string[] | null>(null);

  const { events, loading } = useNostrQuery({
    filters: user
      ? [{
          kinds: [PODCAST_FOLLOW_KIND], authors: [user.pubkey],
          '#d': [PODCAST_FOLLOW_D_TAG], limit: 1,
        }]
      : [],
    enabled: !!user,
    allRelays: true,
  });

  const relayFollows = useMemo(() => {
    if (events.length === 0) return [];
    const newest = [...events].sort((a, b) => b.created_at - a.created_at)[0];
    return parsePodcastFollows(newest);
  }, [events]);

  // Optimistic state wins until the relay round-trip catches up, so the
  // star flips instantly instead of after a publish.
  const following = optimistic ?? relayFollows;

  const isFollowing = useCallback(
    (pubkey: string) => following.includes(pubkey),
    [following],
  );

  const toggle = useCallback(async (pubkey: string): Promise<boolean> => {
    if (!user || saving) return false;
    const current = optimistic ?? relayFollows;
    const next = current.includes(pubkey)
      ? current.filter(pk => pk !== pubkey)
      : [...current, pubkey];

    setOptimistic(next);
    setSaving(true);
    try {
      const { tags, content } = buildPodcastFollowEvent(next);
      await publish({
        kind: PODCAST_FOLLOW_KIND,
        content,
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });
      return true;
    } catch {
      // Roll back so the UI doesn't claim a follow that never landed.
      setOptimistic(current);
      return false;
    } finally {
      setSaving(false);
    }
  }, [user, saving, optimistic, relayFollows, publish]);

  return { following, loading, isFollowing, toggle, saving };
}

/**
 * How many people follow a given show.
 *
 * Counts distinct authors of podcast-follow lists that p-tag this creator.
 * Relay-dependent and therefore approximate — presented as such in the UI.
 */
export function usePodcastFollowerCount(pubkey: string | undefined): {
  count: number;
  loading: boolean;
} {
  const { events, loading } = useNostrQuery({
    filters: pubkey
      ? [{
          kinds: [PODCAST_FOLLOW_KIND], '#t': [PODCAST_FOLLOW_TAG],
          '#p': [pubkey], limit: 500,
        }]
      : [],
    enabled: !!pubkey,
    allRelays: true,
  });

  const count = useMemo(() => {
    if (!pubkey) return 0;
    const authors = new Set<string>();
    for (const ev of events) {
      // Re-verify the p-tag: relays occasionally over-return, and a stale
      // list that no longer includes us must not be counted.
      if (parsePodcastFollows(ev).includes(pubkey)) authors.add(ev.pubkey);
    }
    return authors.size;
  }, [events, pubkey]);

  return { count, loading };
}

/** Follower counts for many shows at once (used to rank the directory). */
export function usePodcastFollowerCounts(pubkeys: string[]): Map<string, number> {
  const key = useMemo(() => pubkeys.slice(0, 100).join(','), [pubkeys]);
  const authors = useMemo(() => (key ? key.split(',') : []), [key]);

  const { events } = useNostrQuery({
    filters: authors.length > 0
      ? [{
          kinds: [PODCAST_FOLLOW_KIND], '#t': [PODCAST_FOLLOW_TAG],
          '#p': authors, limit: 1000,
        }]
      : [],
    enabled: authors.length > 0,
    allRelays: true,
  });

  return useMemo(() => {
    const counts = new Map<string, Set<string>>();
    for (const ev of events) {
      for (const target of parsePodcastFollows(ev)) {
        if (!counts.has(target)) counts.set(target, new Set());
        counts.get(target)!.add(ev.pubkey);
      }
    }
    const result = new Map<string, number>();
    for (const [target, set] of counts) result.set(target, set.size);
    return result;
  }, [events]);
}

// ── Promotion ─────────────────────────────────────────────────────

/** Currently active podcast promotions, highest bid first. */
export function usePodcastBoosts(): {
  boosts: PodcastBoostData[];
  /** creatorPubkey → their best active SHOW promotion. */
  showBoosts: Map<string, PodcastBoostData>;
  loading: boolean;
} {
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 2 * 86400);
  const { events, loading } = useNostrQuery({
    filters: [{ kinds: [PODCAST_BOOST_KIND], '#t': [PODCAST_BOOST_TAG], since, limit: 100 }],
    allRelays: true,
  });

  const boosts = useMemo(() => activePodcastBoosts(events), [events]);

  const showBoosts = useMemo(() => {
    const map = new Map<string, PodcastBoostData>();
    for (const b of boosts) {
      if (b.target !== 'show') continue;
      const existing = map.get(b.podcastPubkey);
      if (!existing || b.priceSats > existing.priceSats) map.set(b.podcastPubkey, b);
    }
    return map;
  }, [boosts]);

  return { boosts, showBoosts, loading };
}

// ── Tips received by a show ───────────────────────────────────────

const TIP_RECEIPT_KIND = 9735;

/**
 * Total sats tipped to each of the given creators (last 180 days).
 *
 * Used to rank "Most Tipped" in discovery and to show a show's earnings in
 * its own analytics. Relay-dependent, so treated as a ranking signal
 * rather than an authoritative ledger.
 */
export function usePodcastTipTotals(pubkeys: string[]): Map<string, number> {
  const key = useMemo(() => pubkeys.slice(0, 100).join(','), [pubkeys]);
  const targets = useMemo(() => (key ? key.split(',') : []), [key]);
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 180 * 86400);

  const { events } = useNostrQuery({
    filters: targets.length > 0
      ? [{ kinds: [TIP_RECEIPT_KIND], '#p': targets, limit: 1000, since }]
      : [],
    enabled: targets.length > 0,
    allRelays: true,
  });

  return useMemo(() => {
    const totals = new Map<string, number>();
    for (const ev of events) {
      const recipient = ev.tags.find(t => t[0] === 'p')?.[1];
      if (!recipient || !targets.includes(recipient)) continue;
      let sats = 0;
      const amountTag = ev.tags.find(t => t[0] === 'amount')?.[1];
      if (amountTag) sats = parseInt(amountTag, 10) || 0;
      if (!sats) {
        try {
          const content = JSON.parse(ev.content || '{}') as { amount_sats?: number };
          sats = content.amount_sats || 0;
        } catch {}
      }
      if (sats > 0) totals.set(recipient, (totals.get(recipient) || 0) + sats);
    }
    return totals;
  }, [events, targets]);
}
